class HeaderAccountPopover extends HTMLElement {
  constructor() {
    super();
    this.trigger = this.querySelector('[data-header-account-popover-trigger]');
    this.panel = this.querySelector('[data-header-account-popover-panel]');
    this.header = null;
    this.headerObserver = null;
    this.pointerInside = false;
    this.closeTimer = null;
    this.hoverMedia = window.matchMedia('(hover: hover) and (min-width: 990px)');
    this.canHover = this.hoverMedia.matches;

    this.onTriggerMouseEnter = this.onTriggerMouseEnter.bind(this);
    this.onTriggerMouseLeave = this.onTriggerMouseLeave.bind(this);
    this.onPanelMouseEnter = this.onPanelMouseEnter.bind(this);
    this.onPanelMouseLeave = this.onPanelMouseLeave.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
    this.onHoverMediaChange = this.onHoverMediaChange.bind(this);
  }

  connectedCallback() {
    if (!this.trigger || !this.panel) return;

    this.header = this.closest('.section-header');
    this.close();
    this.trigger.addEventListener('mouseenter', this.onTriggerMouseEnter);
    this.trigger.addEventListener('mouseleave', this.onTriggerMouseLeave);
    this.panel.addEventListener('mouseenter', this.onPanelMouseEnter);
    this.panel.addEventListener('mouseleave', this.onPanelMouseLeave);
    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('focusin', this.onFocusIn);
    this.panel.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);

    if (this.header && 'MutationObserver' in window) {
      this.headerObserver = new MutationObserver(() => {
        if (this.header.classList.contains('shopify-section-header-hidden')) this.close();
      });
      this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });
    }

    if (this.hoverMedia.addEventListener) {
      this.hoverMedia.addEventListener('change', this.onHoverMediaChange);
    } else {
      this.hoverMedia.addListener(this.onHoverMediaChange);
    }
  }

  disconnectedCallback() {
    this.clearCloseTimer();
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    this.trigger?.removeEventListener('mouseenter', this.onTriggerMouseEnter);
    this.trigger?.removeEventListener('mouseleave', this.onTriggerMouseLeave);
    this.panel?.removeEventListener('mouseenter', this.onPanelMouseEnter);
    this.panel?.removeEventListener('mouseleave', this.onPanelMouseLeave);
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.trigger?.removeEventListener('focusin', this.onFocusIn);
    this.panel?.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);

    if (this.hoverMedia.removeEventListener) {
      this.hoverMedia.removeEventListener('change', this.onHoverMediaChange);
    } else {
      this.hoverMedia.removeListener(this.onHoverMediaChange);
    }
  }

  onTriggerMouseEnter() {
    this.pointerInside = true;
    if (this.canHover) this.open();
  }

  onTriggerMouseLeave() {
    this.pointerInside = false;
    if (this.canHover) this.scheduleClose();
  }

  onPanelMouseEnter() {
    this.pointerInside = true;
    if (this.canHover) this.open();
  }

  onPanelMouseLeave() {
    this.pointerInside = false;
    if (this.canHover) this.scheduleClose();
  }

  onTriggerClick(event) {
    if (this.canHover && event.detail > 0) {
      this.trigger.blur();
      return;
    }

    event.preventDefault();
    const mobileSearchWasClosed = this.closeOpenMobileSearch();

    if (mobileSearchWasClosed) {
      this.open();
      return;
    }

    const mobileMenuWasClosed = this.closeOpenMobileMenu(event);

    if (mobileMenuWasClosed) {
      this.open();
      return;
    }

    this.isOpen() ? this.close() : this.open();
  }

  closeOpenMobileSearch() {
    if (!window.matchMedia('(max-width: 989px)').matches) return false;

    const searchModal = Array.from(this.header?.querySelectorAll('details-modal') || [])
      .find((modal) => typeof modal.isOpen === 'function' && modal.isOpen());

    if (!searchModal || typeof searchModal.close !== 'function') return false;

    searchModal.close(false);
    return true;
  }

  closeOpenMobileMenu(event) {
    if (!window.matchMedia('(max-width: 989px)').matches) return false;

    const headerDrawer = this.header?.querySelector('#header-drawer-menu-mobile')
      || document.getElementById('header-drawer-menu-mobile');
    const drawerDetails = headerDrawer?.querySelector(':scope > details.menu-drawer-container');
    const isDrawerOpen = Boolean(
      drawerDetails?.hasAttribute('open')
      || drawerDetails?.classList.contains('menu-opening')
      || this.header?.classList.contains('menu-open')
    );

    if (!headerDrawer || !drawerDetails || !isDrawerOpen) return false;

    if (typeof headerDrawer.closeMenuDrawer === 'function') {
      headerDrawer.closeMenuDrawer(event);
    }

    // The theme's native close waits 400ms before removing `open`. When the
    // account popover opens during that interval, summary::before remains as a
    // full-screen dimmer. Reset the complete drawer state synchronously.
    drawerDetails.querySelectorAll('details').forEach((details) => {
      details.classList.remove('menu-opening');
      details.removeAttribute('open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
    drawerDetails.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    drawerDetails.classList.remove('menu-opening');
    drawerDetails.removeAttribute('open');
    drawerDetails.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');

    const headerElement = this.header?.querySelector('.header');
    const headerInner = this.header?.querySelector('.header__inner');
    this.header?.classList.remove('menu-open');
    headerElement?.classList.remove('header-active');
    headerInner?.classList.remove('header-drawer-open');
    document.documentElement.classList.remove('overflow-hidden-tablet', 'no-scroll');
    document.body.classList.remove('overflow-hidden-tablet', 'open-submenu-mobile');

    return true;
  }

  onFocusIn() {
    this.open();
  }

  onFocusOut() {
    window.setTimeout(() => {
      if (this.pointerInside) return;
      if (!this.isPopoverTarget(document.activeElement)) this.close();
    });
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
    this.trigger.focus();
  }

  onDocumentPointerDown(event) {
    if (!this.canHover && this.isOpen() && !this.isPopoverTarget(event.target)) {
      this.close();
    }
  }

  onHoverMediaChange(event) {
    this.canHover = event.matches;
    if (!this.canHover) this.close();
  }

  isOpen() {
    return this.dataset.open === 'true';
  }

  isPopoverTarget(target) {
    return Boolean(target) && (this.trigger.contains(target) || this.panel.contains(target));
  }

  open() {
    if (this.header?.classList.contains('shopify-section-header-hidden')) return;

    this.clearCloseTimer();
    this.dataset.open = 'true';
    this.trigger.setAttribute('aria-expanded', 'true');
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.inert = false;
  }

  close() {
    this.clearCloseTimer();
    this.dataset.open = 'false';
    this.trigger?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('aria-hidden', 'true');
    if (this.panel) this.panel.inert = true;
  }

  scheduleClose() {
    this.clearCloseTimer();
    this.closeTimer = window.setTimeout(() => {
      if (!this.isPopoverTarget(document.activeElement)) this.close();
    }, 260);
  }

  clearCloseTimer() {
    if (this.closeTimer === null) return;

    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }
}

if (!customElements.get('header-account-popover')) {
  customElements.define('header-account-popover', HeaderAccountPopover);
}
